import React, { useState } from 'react';
import PropTypes from 'prop-types';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';

import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import { useLob } from '~/components/hooks/useLob';
import {
  changedFieldGroupValues,
  fieldGroupKeys,
} from '~/components/IncidentConfiguration/HierarchyFieldGroup/hierarchyTree';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isEmptyValues, isFeatureEnabled } from '~/Utils';

import { useClaim } from '../../../ClaimContainer';
import { FsMenu } from '../../../core';
import { cleanConfiguredFieldsFormikValuesBeforeSend } from '../../../IncidentConfiguration/ConfiguredFields';
import AdditionalInformationDialog from '../AdditionalInformation/AdditionalInformationDialog';
import {
  CustomSectionFragment,
  getConfiguredFieldsForSection,
  getCustomAdditionalInformationSections,
  getCustomSectionInitialValues,
  getCustomSectionValidationSchema,
  getFieldGroupsForSection,
  getIsCustomSectionActive,
  getIsCustomSectionEnabled,
} from '../AdditionalInformation/Fragments/CustomSectionFragment';
import {
  getInitialAccidentFollowUpInitialValidationSchema,
  getInitialAccidentFollowUpInitialValues,
  getInitialAccidentFollowUpSectionConfig,
  InitialAccidentFollowUpFragment,
} from '../AdditionalInformation/Fragments/InitialAccidentFollowUpFragment';
import {
  getInitialTheftFollowUpInitialValidationSchema,
  getInitialTheftFollowUpInitialValues,
  getInitialTheftFollowUpSectionConfig,
  InitialTheftFollowUpFragment,
} from '../AdditionalInformation/Fragments/InitialTheftFollowUpFragment';
import {
  getPoliceFireDepartmentInvolvementInitialValues,
  getPoliceFireDepartmentInvolvementSectionConfig,
  getPoliceFireDepartmentInvolvementValidationSchema,
  PoliceFireDepartmentInvolvementFragment,
} from '../AdditionalInformation/Fragments/PoliceFireDepartmentInvolvementFragment';
import {
  getIsSupplementaryInformationSectionActive,
  getSupplementaryInformationFieldGroups,
  getSupplementaryInformationInitialValues,
  getSupplementaryInformationValidationSchema,
  SupplementaryInformationFragment,
} from '../AdditionalInformation/Fragments/SupplementaryInformationFragment';

// Menu entry ids of auto's built-in sections. An entry id is the menu's React key and sits beside the
// custom sections' ids in the same list, so every one of these is reserved against custom section ids
// (pinned by reservedRuntimeIds.test.ts).
export const AUTO_MORE_DETAILS_ENTRY_IDS = {
  policeAndFireDepartment: 'police_and_fire_department_involvement',
  theftFollowUp: 'theft_follow_up',
  accidentFollowUp: 'accident_follow_up',
};

// `isEmptyValues` (shared, from Utils) treats nil, '', [] and a plain {} as empty. The {} case matters for
// location fields: GlobalLossLocation's Clear button sets the value to {}, and the backend clears a location
// only when the value is null (update_location_field_by_config), so a cleared location must go out as null.
//
// Build the configured_fields_values PATCH for one custom section. The backend PATCH is a per-key merge
// (incident_configuration_manager.patch_configured_fields), so only the keys we send are touched. We keep
// filled values (via the shared cleaner, which also carries the contact-id companion field), send explicit
// `null` for fields the user cleared so the merge overwrites the stored value, and drop never-answered
// fields so a no-op Save writes nothing. Only the section's rendered fields are considered, so a field the
// dialog does not show (inactive or out of the current sub-type) keeps its stored value through the merge.
const buildCustomSectionFieldsPatch = (renderedSectionFields, formValues, priorValues) => {
  const patch = cleanConfiguredFieldsFormikValuesBeforeSend(renderedSectionFields, formValues);
  renderedSectionFields.forEach((field) => {
    if (!isEmptyValues(formValues[field.id])) {
      return;
    }
    if (isEmptyValues(priorValues[field.id])) {
      delete patch[field.id];
    } else {
      patch[field.id] = null;
    }
  });
  return patch;
};

function IncidentMoreDetailsMenu(props) {
  const { incident, onUpdateFields } = props;
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { lob } = useLob();
  const [isUpdating, setIsUpdating] = useState(false);
  const [openInitialTheftFollowUp, setOpenInitialTheftFollowUp] = useState(false);
  const [openSupplementaryInformation, setOpenSupplementaryInformation] = useState(false);
  const [openInitialAccidentFollowUp, setOpenInitialAccidentFollowUp] = useState(false);

  const [openPoliceFireDepartmentInvolvement, setOpenPoliceFireDepartmentInvolvement] = useState(false);
  // Custom sections are dynamic, so a single "which section is open" id replaces the per-section booleans.
  const [openCustomSectionId, setOpenCustomSectionId] = useState(null);

  const { claim } = useClaim();
  const { userOrganization } = useCms();
  const isHierarchyFieldGroupsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS
  );
  const supplementaryFieldGroups = getSupplementaryInformationFieldGroups(
    incidentConfiguration,
    isFieldSupportedByCategories,
    isHierarchyFieldGroupsEnabled
  );
  const supplementaryFieldGroupKeys = fieldGroupKeys(supplementaryFieldGroups);

  const currentSupplementaryInformation = {
    was_replacement_car_rented: incident?.was_replacement_car_rented,
    replacement_car_rental_date: incident?.replacement_car_rental_date,
    are_convictions_or_claims_in_the_last_5_years: incident?.are_convictions_or_claims_in_the_last_5_years,
    convictions_or_claim_description: incident?.convictions_or_claim_description,
    did_anyone_take_photos_or_video: incident?.did_anyone_take_photos_or_video,
    was_cctv_footage: incident?.was_cctv_footage,
    can_take_photos_of_damage: incident?.can_take_photos_of_damage,
    other_policy_description: incident?.other_policy_description,
    photographer_contact_id: incident?.photographer_contact_id,
    photographer_contact_full_name: incident?.photographer_contact_full_name,
    is_other_policy_covering: incident?.is_other_policy_covering,
    is_notification_only: incident?.is_notification_only,
    is_policyholder_occupation_right: incident?.is_policyholder_occupation_right,
    policyholder_current_occupation: incident?.policyholder_current_occupation,
    does_policyholder_second_occupation_exist: incident?.does_policyholder_second_occupation_exist,
    policyholder_second_occupation: incident?.policyholder_second_occupation,
    // The groups this dialog renders open on their stored selection; every other group stays out of it.
    configured_fields_values: omit(
      incident?.configured_fields_values,
      (incidentConfiguration.incident_details.field_groups ?? [])
        .map((group) => group.id)
        .filter((groupId) => !supplementaryFieldGroupKeys.includes(groupId))
    ),
  };
  const currentPoliceFireDepartmentInvolvement = {
    were_police_involved: incident?.were_police_involved,
    police_crime_reference_number: incident?.police_crime_reference_number,
    police_department_contact_id: incident?.police_department_contact_id,
    police_report: incident?.police_report,
    did_speak_with_officer: incident?.did_speak_with_officer,
    was_ticket_received: incident?.was_ticket_received,
    violation_title: incident?.violation_title,
    police_description: incident?.police_description,
    were_fire_department_involved: incident?.were_fire_department_involved,
    fire_department_description: incident?.fire_department_description,
    were_any_hazardous_good_being_carried: incident?.were_any_hazardous_good_being_carried,
    pending_prosecutions_or_investigations_exists: incident?.pending_prosecutions_or_investigations_exists,
  };
  const currentTheftFollowUp = {
    date_vehicle_last_seen: incident?.date_vehicle_last_seen,
    time_vehicle_last_seen: incident?.time_vehicle_last_seen,
    vehicle_last_seen_location: incident?.vehicle_last_seen_location,
    keys_location: incident?.keys_location,
    vehicle_windows_closed_and_secured: incident?.vehicle_windows_closed_and_secured,
    vehicle_fitted_with_security_devices: incident?.vehicle_fitted_with_security_devices,
    security_devices_description: incident?.security_devices_description,
    vehicle_fitted_with_tracking_devices: incident?.vehicle_fitted_with_tracking_devices,
    tracking_devices_description: incident?.tracking_devices_description,
    is_vehicle_for_sale: incident?.is_vehicle_for_sale,
    were_belongings_stolen_from_vehicle: incident?.were_belongings_stolen_from_vehicle,
    belongings_stolen: incident?.belongings_stolen,
    discovered_the_loss_contact_id: incident?.discovered_the_loss_contact_id,
    crime_reference_number: incident?.crime_reference_number,
    num_of_keys_received_on_purchase: incident?.num_of_keys_received_on_purchase,
    current_num_of_keys: incident?.current_num_of_keys,
    was_vehicle_found_with_keys: incident?.was_vehicle_found_with_keys,
    date_noticed_vehicle_missing: incident?.date_noticed_vehicle_missing,
    time_noticed_vehicle_missing: incident?.time_noticed_vehicle_missing,
    person_liable_for_the_theft_is_known: incident?.person_liable_for_the_theft_is_known,
    note: incident?.note,
  };
  const currentAccidentFollowUp = {
    journey_purpose: incident?.journey_purpose,
    weather_conditions: incident?.weather_conditions,
    direction_ph_vehicle_was_going: incident?.direction_ph_vehicle_was_going,
    was_ambulance_called: incident?.was_ambulance_called,
    first_party_way_sign_or_a_headlight: incident?.first_party_way_sign_or_a_headlight,
    street_condition: incident?.street_condition,
    was_first_party_driver_using_the_phone: incident?.was_first_party_driver_using_the_phone,
    were_first_party_vehicle_lights_on: incident?.were_first_party_vehicle_lights_on,
    was_anyone_under_the_influence_of_drugs_alcohol: incident?.was_anyone_under_the_influence_of_drugs_alcohol,
  };

  const handleUpdatingFields = async (fieldsValues) => {
    setIsUpdating(true);

    try {
      await onUpdateFields(fieldsValues);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInitialTheftFollowUp = () => {
    setOpenInitialTheftFollowUp(true);
  };

  const handleSupplementaryInformation = () => {
    setOpenSupplementaryInformation(true);
  };

  const handlePoliceFireDepartmentInvolvement = () => {
    setOpenPoliceFireDepartmentInvolvement(true);
  };

  const handleInitialAccidentFollowUp = () => {
    setOpenInitialAccidentFollowUp(true);
  };

  const newUIGeneralAdditionalInfo = [
    {
      id: 'supplementary_information',
      label: 'Supplementary Information',
      onClick: handleSupplementaryInformation,
      disabledFunc: () => false,
      displayFunc: () =>
        getIsSupplementaryInformationSectionActive(
          incidentConfiguration,
          isHierarchyFieldGroupsEnabled,
          isFieldSupportedByCategories
        ),
    },
  ];

  let newUIAutoAdditionalInfo = [
    {
      id: AUTO_MORE_DETAILS_ENTRY_IDS.policeAndFireDepartment,
      label: 'Police and Fire Department Involvement',
      onClick: handlePoliceFireDepartmentInvolvement,
      disabledFunc: () =>
        !isFieldSupportedByCategories(getPoliceFireDepartmentInvolvementSectionConfig(incidentConfiguration)),
      displayFunc: () => getPoliceFireDepartmentInvolvementSectionConfig(incidentConfiguration)?.active,
    },
    {
      id: AUTO_MORE_DETAILS_ENTRY_IDS.theftFollowUp,
      label: 'Theft Follow-Up',
      onClick: handleInitialTheftFollowUp,
      disabledFunc: () => !isFieldSupportedByCategories(getInitialTheftFollowUpSectionConfig(incidentConfiguration)),
      displayFunc: () => getInitialTheftFollowUpSectionConfig(incidentConfiguration)?.active,
    },
    {
      id: AUTO_MORE_DETAILS_ENTRY_IDS.accidentFollowUp,
      label: 'Accident Follow-Up',
      onClick: handleInitialAccidentFollowUp,
      disabledFunc: () => !isFieldSupportedByCategories(getInitialAccidentFollowUpSectionConfig(incidentConfiguration)),
      displayFunc: () => getInitialAccidentFollowUpSectionConfig(incidentConfiguration)?.active,
    },
  ];

  // Config-driven custom sections, every LOB (on auto they follow the built-in entries). Each shows in
  // the menu when it is active with >=1 rendered field, and is disabled (not hidden) when the claim's
  // sub-type leaves no field renderable — mirroring the auto entries' displayFunc/disabledFunc pair.
  const customSections = getCustomAdditionalInformationSections(incidentConfiguration);
  const customSectionMenuEntries = customSections.map((section) => ({
    id: section.id,
    label: section.desc || section.id,
    onClick: () => setOpenCustomSectionId(section.id),
    disabledFunc: () =>
      !getIsCustomSectionEnabled(
        incidentConfiguration,
        section,
        isFieldSupportedByCategories,
        isHierarchyFieldGroupsEnabled
      ),
    displayFunc: () => getIsCustomSectionActive(incidentConfiguration, section, isHierarchyFieldGroupsEnabled),
  }));

  const openCustomSection = customSections.find((section) => section.id === openCustomSectionId);
  // Match what ConfiguredFields actually renders: it drops fields with active === false, and the dialog
  // filters by sub-type. Using this one predicate for the seed, the render, the validation and the PATCH
  // keeps them consistent, so an inactive field never enters any of them.
  const openCustomSectionOmitFn = (field) => field?.active !== false && isFieldSupportedByCategories(field);
  const openCustomSectionRenderedFields = openCustomSection
    ? getConfiguredFieldsForSection(incidentConfiguration, openCustomSection.id).filter(openCustomSectionOmitFn)
    : [];
  const openCustomSectionFieldGroups = openCustomSection
    ? getFieldGroupsForSection(
        incidentConfiguration,
        openCustomSection.id,
        openCustomSectionOmitFn,
        isHierarchyFieldGroupsEnabled
      )
    : [];
  // Seed only THIS section's rendered fields and groups (empty per field type), then overlay the section's own saved
  // values, coalescing a stored null back to the empty seed so a null never flips an input from controlled to
  // uncontrolled and never crashes a multiselect (its [] seed). Other sections stay out of the payload — the
  // per-key backend merge preserves them.
  const openCustomSectionInitialValues = openCustomSection
    ? mapValues(
        getCustomSectionInitialValues(
          incidentConfiguration,
          openCustomSection,
          openCustomSectionOmitFn,
          isHierarchyFieldGroupsEnabled
        ).configured_fields_values,
        (emptySeed, fieldId) => {
          const saved = incident?.configured_fields_values?.[fieldId];
          return saved === undefined || saved === null ? emptySeed : saved;
        }
      )
    : {};

  const getNewUIAdditionalInfo = () => {
    switch (claim.type) {
      case 'auto_claim':
        return [...newUIGeneralAdditionalInfo, ...newUIAutoAdditionalInfo, ...customSectionMenuEntries];
      default:
        return [...newUIGeneralAdditionalInfo, ...customSectionMenuEntries];
    }
  };

  return (
    <>
      {getNewUIAdditionalInfo()?.some(({ displayFunc }) => displayFunc()) ? (
        <FsMenu
          buttonTitle="More Details"
          items={getNewUIAdditionalInfo()?.map(({ id, label, onClick, disabledFunc, displayFunc }) => ({
            id,
            label,
            onClick,
            disabled: isUpdating,
            disabledFunc,
            displayFunc,
          }))}
          ignorePermissions
        />
      ) : null}
      {openPoliceFireDepartmentInvolvement && (
        <AdditionalInformationDialog
          initialValues={currentPoliceFireDepartmentInvolvement}
          open={openPoliceFireDepartmentInvolvement}
          onCancel={() => setOpenPoliceFireDepartmentInvolvement(false)}
          onSubmit={async (values) => {
            await handleUpdatingFields(values);
            setOpenPoliceFireDepartmentInvolvement(false);
          }}
          title="Police and Fire Department Involvement"
          additionalFieldOmitFn={(field) => isFieldSupportedByCategories(field)}
          AdditionalInformationFragment={PoliceFireDepartmentInvolvementFragment}
          AdditionalInformationFragmentInitialValues={getPoliceFireDepartmentInvolvementInitialValues(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
          AdditionalInformationFragmentValidationSchema={getPoliceFireDepartmentInvolvementValidationSchema(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
        />
      )}
      {openInitialTheftFollowUp && (
        <AdditionalInformationDialog
          initialValues={currentTheftFollowUp}
          open={openInitialTheftFollowUp}
          onCancel={() => setOpenInitialTheftFollowUp(false)}
          onSubmit={async (values) => {
            await handleUpdatingFields(values);
            setOpenInitialTheftFollowUp(false);
          }}
          title="Theft Follow Up"
          additionalFieldOmitFn={(field) => isFieldSupportedByCategories(field)}
          AdditionalInformationFragment={InitialTheftFollowUpFragment}
          AdditionalInformationFragmentInitialValues={getInitialTheftFollowUpInitialValues(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
          AdditionalInformationFragmentValidationSchema={getInitialTheftFollowUpInitialValidationSchema(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
        />
      )}
      {openSupplementaryInformation && (
        <AdditionalInformationDialog
          initialValues={currentSupplementaryInformation}
          open={openSupplementaryInformation}
          onCancel={() => setOpenSupplementaryInformation(false)}
          onSubmit={async (values) => {
            // The dialog holds every group it renders, stored ones included, so only the groups the user
            // changed go out. Without a group to render the values pass through as they always have.
            await handleUpdatingFields(
              supplementaryFieldGroups.length > 0
                ? {
                    ...values,
                    configured_fields_values: {
                      ...omit(values.configured_fields_values, supplementaryFieldGroupKeys),
                      ...changedFieldGroupValues(
                        supplementaryFieldGroups,
                        values.configured_fields_values,
                        incident?.configured_fields_values
                      ),
                    },
                  }
                : values
            );
            setOpenSupplementaryInformation(false);
          }}
          title="Supplementary Information"
          additionalFieldOmitFn={(field) => isFieldSupportedByCategories(field)}
          AdditionalInformationFragment={SupplementaryInformationFragment}
          claimId={claim.id}
          AdditionalInformationFragmentInitialValues={getSupplementaryInformationInitialValues(
            incidentConfiguration,
            lob,
            isFieldSupportedByCategories,
            isHierarchyFieldGroupsEnabled
          )}
          AdditionalInformationFragmentValidationSchema={getSupplementaryInformationValidationSchema(
            incidentConfiguration,
            lob,
            isFieldSupportedByCategories,
            isHierarchyFieldGroupsEnabled
          )}
        />
      )}
      {openInitialAccidentFollowUp && (
        <AdditionalInformationDialog
          initialValues={currentAccidentFollowUp}
          open={openInitialAccidentFollowUp}
          onCancel={() => setOpenInitialAccidentFollowUp(false)}
          onSubmit={async (values) => {
            await handleUpdatingFields(values);
            setOpenInitialAccidentFollowUp(false);
          }}
          title="Initial Accident Follow Up"
          additionalFieldOmitFn={(field) => isFieldSupportedByCategories(field)}
          AdditionalInformationFragment={InitialAccidentFollowUpFragment}
          AdditionalInformationFragmentInitialValues={getInitialAccidentFollowUpInitialValues(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
          AdditionalInformationFragmentValidationSchema={getInitialAccidentFollowUpInitialValidationSchema(
            incidentConfiguration,
            isFieldSupportedByCategories
          )}
        />
      )}
      {openCustomSection && (
        <AdditionalInformationDialog
          initialValues={{ configured_fields_values: openCustomSectionInitialValues }}
          open
          onCancel={() => setOpenCustomSectionId(null)}
          onSubmit={async (values) => {
            await handleUpdatingFields({
              ...values,
              configured_fields_values: {
                ...buildCustomSectionFieldsPatch(
                  openCustomSectionRenderedFields,
                  values.configured_fields_values,
                  incident?.configured_fields_values ?? {}
                ),
                ...changedFieldGroupValues(
                  openCustomSectionFieldGroups,
                  values.configured_fields_values,
                  incident?.configured_fields_values
                ),
              },
            });
            setOpenCustomSectionId(null);
          }}
          title={openCustomSection.desc || openCustomSection.id}
          additionalFieldOmitFn={openCustomSectionOmitFn}
          AdditionalInformationFragment={CustomSectionFragment}
          id={openCustomSection.id}
          claimId={claim.id}
          AdditionalInformationFragmentValidationSchema={getCustomSectionValidationSchema(
            incidentConfiguration,
            openCustomSection,
            openCustomSectionOmitFn,
            isHierarchyFieldGroupsEnabled
          )}
        />
      )}
    </>
  );
}

IncidentMoreDetailsMenu.propTypes = {
  incident: PropTypes.object.isRequired,
  onUpdateFields: PropTypes.func.isRequired,
};

export { buildCustomSectionFieldsPatch };
export default IncidentMoreDetailsMenu;
